import {
  configureStore,
  getDefaultMiddleware,
  AnyAction,
} from "@reduxjs/toolkit"
import type { PreloadedState } from "@reduxjs/toolkit"
import { combineReducers } from "redux"
import storage from "redux-persist/lib/storage"
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist"
import { createReduxHistoryContext } from "redux-first-history"
import { createBrowserHistory } from "history"
import { ThunkDispatch } from "redux-thunk"

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({
    history: createBrowserHistory(),
  })

const middlewares = [
  ...getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
    },
  }),
  routerMiddleware,
]

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { logger } = require("redux-logger")

  middlewares.push(logger)
}

import authReducer from "redux/features/auth/authSlice"
import cookieConsentReducer from "redux/features/cookie/cookieConsentSlice"
import pollingReducer from "redux/features/polling/pollingSlice"
import { features } from "redux/features"

const rootReducer = combineReducers({
  router: routerReducer,
  auth: authReducer,
  consent: cookieConsentReducer,
  polling: pollingReducer,
  features,
})

const persistConfig = {
  key: "root",
  version: 1,
  whitelist: ["auth", "consent"],
  storage,
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: persistedReducer,
    middleware: middlewares,
    preloadedState,
  })
}

export const store = setupStore()
export const persistor = persistStore(store)
export const history = createReduxHistory(store)

export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof setupStore>
export type AppThunkDispatch = ThunkDispatch<RootState, never, AnyAction>
